import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ButtonsHeader from "@/app/components/common/ButtonsHeader";
import { footerBg3Style } from "@/assets";
import ProductListing from "@/app/components/product/ProductListing";
import ProductListingSkeleton from "@/app/components/product/ProductListingSkeleton";
import Categories from "../components/categories/Categories";
import CategoriesSkeleton from "../components/categories/CategoriesSkeleton";
import { useShowInitialLoader } from "@/lib/hooks";
import {
  useGetCategoriesQuery,
  useGetSubSubCategoryQuery,
} from "@/lib/api/apiSlice";
import {
  defaultPaginationOptions,
  endpoints,
  ProductStatus,
} from "muminsmart-utils";

export default function CategoriesPage() {
  const params = useParams<{
    categoryId?: string;
    subCategoryId?: string;
    subSubCategoryId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const categoryId =
    params.categoryId ?? searchParams.get("categoryId") ?? undefined;
  const subCategoryId =
    params.subCategoryId ?? searchParams.get("subCategoryId") ?? undefined;
  const subSubCategoryId =
    params.subSubCategoryId ??
    searchParams.get("subSubCategoryId") ??
    undefined;

  const isProductView = Boolean(
    categoryId && subCategoryId && subSubCategoryId,
  );

  const {
    data: categories,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetCategoriesQuery(undefined, { skip: isProductView });

  const {
    data: subSubCategory,
    isLoading: isSubSubCategoryLoading,
    isError: isSubSubCategoryError,
  } = useGetSubSubCategoryQuery(subSubCategoryId!, {
    skip: !isProductView,
  });

  const showCategoriesLoader = useShowInitialLoader(
    isCategoriesLoading,
    categories,
  );
  const showSubSubCategoryLoader = useShowInitialLoader(
    isSubSubCategoryLoading,
    subSubCategory,
  );

  const productFetchConfig = useMemo(
    () =>
      isProductView
        ? {
            endpoint: endpoints.productVariant.paginated,
            query: {
              ...defaultPaginationOptions,
              sortBy: "createdAt",
              sortOrder: "DESC",
              categoryId,
              subCategoryId,
              subSubCategoryId,
              relations: "product,variantAttributes",
              isActive: true,
              status: ProductStatus.Verified,
            },
            mode: "variant" as const,
            errorMessage: "Error fetching products",
          }
        : undefined,
    [categoryId, subCategoryId, subSubCategoryId, isProductView],
  );

  if (isProductView) {
    if (showSubSubCategoryLoader) {
      return (
        <div className="min-h-screen">
          <ButtonsHeader heading="Category" />
          <div
            className="mt-4 min-h-[calc(100vh-5rem)] rounded-t-3xl bg-white py-6"
            style={footerBg3Style}
          >
            <div className="px-4">
              <ProductListingSkeleton
                showSearch
                className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
              />
            </div>
          </div>
        </div>
      );
    }

    if (isSubSubCategoryError) {
      return (
        <div className="flex min-h-screen items-center justify-center px-4">
          <p className="text-lg">Failed to fetch sub-sub-category</p>
        </div>
      );
    }

    return (
      <div className="min-h-screen">
        <ButtonsHeader heading={subSubCategory?.name ?? "Category"} />
        <div
          className="py-6 mt-4 rounded-t-3xl bg-white min-h-[calc(100vh-5rem)]"
          style={footerBg3Style}
        >
          <div className="px-4">
            <ProductListing
              fetchConfig={productFetchConfig}
              className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
              showSearch={true}
            />
          </div>
        </div>
      </div>
    );
  }

  if (showCategoriesLoader) {
    return (
      <>
        <ButtonsHeader heading="Categories" />
        <CategoriesSkeleton />
      </>
    );
  }

  if (isCategoriesError) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <p className="text-lg">Failed to fetch categories</p>
      </div>
    );
  }

  return (
    <>
      <ButtonsHeader heading="Categories" />
      <Categories categories={categories ?? []} />
    </>
  );
}
